import type { SavedObjectiveRules } from 'definitions';
import { WEBSITE_DEFAULT_LABEL } from 'helpers/objectives-identity';

export const MAX_SAVED_OBJECTIVE_NAME_LENGTH = 80;

type Named = { id: number; name: string; rules: SavedObjectiveRules };

/**
 * One line saying what saved objectives hold: "purchase · 3 secondary". The primary is named, the
 * secondaries counted. A null primary reads as the website default, which it stays when loaded.
 */
export function summarizeSavedObjectives({ primaryGoal, secondaryGoals }: SavedObjectiveRules): string {
  const primary = primaryGoal ?? WEBSITE_DEFAULT_LABEL;
  const count = secondaryGoals.length;
  return count ? `${primary} · ${count} secondary` : `${primary} · no secondary`;
}

/** Entries whose name or any objective contains the query, ignoring case. */
export function filterSavedObjectives<T extends Named>(list: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...list];
  return list.filter(
    ({ name, rules }) =>
      name.toLowerCase().includes(needle) ||
      (rules.primaryGoal ?? WEBSITE_DEFAULT_LABEL).toLowerCase().includes(needle) ||
      rules.secondaryGoals.some((goal) => goal.toLowerCase().includes(needle)),
  );
}

type NameErrorParams = { name: string; list: readonly Named[]; exceptId?: number };

/**
 * Why a name cannot be used, or null. Mirrors the API (1 to 80 characters, unique on the website
 * ignoring case) so the dialog can say so inline before a request; the API still decides.
 */
export function savedObjectivesNameError({ name, list, exceptId }: NameErrorParams): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_SAVED_OBJECTIVE_NAME_LENGTH) return 'Keep the name under 80 characters.';
  const clash = list.some((entry) => entry.id !== exceptId && entry.name.toLowerCase() === trimmed.toLowerCase());
  return clash
    ? 'Saved objectives with this name already exist. Pick another name or switch to Update existing.'
    : null;
}
